// Demo version of conversations categories edit page
import React, { useCallback } from 'react';
import axios from '../../helpers/AxiosInstance';
import { Grid, Cell } from 'react-foundation';
import { PlusCircle } from 'react-bootstrap-icons';
import '../../stylesheets/components/Categories.css';
import { useNavigate, Link } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';
import { Modal } from 'react-bootstrap';

// Components
import {
  Header,
  DluchApi,
  FlashMessage,
  ProgressBar,
  ErrorRender,
  setToken,
  filteredCategories,
} from '../../constants/SharedComponents';

import QuestionCategory from '../../components/QuestionCategory';
import QuestionCategoryContent from '../../components/QuestionCategoryContent';
import { useDemoContext } from '../../contexts/DemoContext';
import useCurrentUser from '../../hooks/useCurrentUser';

export function DemoConversationsCategoriesEdit() {
  const navigate = useNavigate();
  const { nextDemoStep, updateDemoPhase } = useDemoContext();
  const currentUser = useCurrentUser();

  const handleGoBack = useCallback(() => {
    navigate('/demo/conversations');
  }, [navigate]);

  // Demo state matching production structure
  const [flashMessageObject] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [selectedCategories, setSelectedCategories] = React.useState([]);
  const [deselectedCategories, setDeselectedCategories] = React.useState([]);
  const [currentModal, setCurrentModal] = React.useState(null);
  const [submissionUnlocked] = React.useState(true); // Demo always has unlocked submission
  const [guidedRoute] = React.useState(false); // Self-reflection route
  const [inputs, setInputs] = React.useState({
    general_notes: '' // Initialize general notes field
  });
  const [formChanged, setFormChanged] = React.useState(false);
  const [allCategories, setAllCategories] = React.useState([]);

  // Create refs properly
  const categoryFormButtons = React.useRef({});

  // API setup for demo (using demo conversation ID)
  const conversationId = 'demo-conversation-123';
  const headerParams = currentUser?.token ? setToken(currentUser.token) : {};

  // Get categories from real API
  const getQuestionCategoriesData = async () => {
    setLoading(true);

    try {
      let Api = new DluchApi('QuestionsCategoriesIndex', conversationId);
      const response = await axios.get(Api.ApiRequestUrl(), headerParams);
      let responseData = response.data.content.categories;
      let categories = filteredCategories(responseData, 3, false);
      setAllCategories(categories);
      setDeselectedCategories(categories);
    } catch (err) {
      // Fallback to demo data if API fails
      console.warn('API call failed, using fallback demo data:', err);
      const fallbackCategories = [
        {
          id: 'ee048b5c-97b2-4f1e-bf90-0da6169729f8',
          name: "Control",
          description: "Control refers to the extent to which you can decide things about your work. Control can have various forms: it can be whether you have discretion on your working patterns, whether you can decide how to do things in your work.",
          categoryType: 0,
          selfGuided: false
        },
        {
          id: 'bb844672-2318-4a9f-b9ac-354f535c7dea',
          name: "Management Support",
          description: "Management support refers to the extent you feel that your line manager is providing you with appropriate support. Support can be technical advice, sufficient resources, information, emotional help.",
          categoryType: 0,
          selfGuided: false
        },
        {
          id: 'bae25492-5633-48ec-b04f-69f2bc9bc43b',
          name: "Peer Support",
          description: "Peer support is about the extent to which you perceive your colleagues to be supportive. It refers to the social support, encouragement and resources provided by work colleagues.",
          categoryType: 0,
          selfGuided: false
        },
        {
          id: '9608c2cc-6822-4a22-9ebf-4e5d7cab50f3',
          name: "Role",
          description: "Role refers to clarity about what is expected from you to do your job and how your work contributes to the wider organisation. In other words, what type of behaviours you should adopt to do your job well.",
          categoryType: 0,
          selfGuided: false
        },
        {
          id: '4af58c57-cbe4-499e-99ec-fb33c45c1b98',
          name: "Change",
          description: "Change relates about how workplace changes are managed. Change could include major organisations-level changes, such as restructures, merger, acquisition, new physical workspace, or new management.",
          categoryType: 0,
          selfGuided: false
        },
        {
          id: '8d0b0089-2cd2-4c4f-97ff-588710cba91d',
          name: "Demands",
          description: "Demands refers to aspects of your job that require effort. Work demands are very broad and varied across different jobs and they are really the core of what your job is about.",
          categoryType: 0,
          selfGuided: false
        },
        {
          id: '7185abba-d609-4071-b6b9-6e199c475e38',
          name: "Relationships",
          description: "Relationships refers to the quality of workplace relationships. Ideally, people should be working in a positive work atmosphere in which conflict is properly resolved and unacceptable behaviour is not tolerated.",
          categoryType: 0,
          selfGuided: false
        },
        {
          id: 'personalised-1',
          name: "Personalised topic",
          description: "Add your own personalized topic for discussion.",
          categoryType: 1,
          selfGuided: true
        },
        {
          id: 'personalised-2',
          name: "Personalised topic",
          description: "Add your own personalized topic for discussion.",
          categoryType: 1,
          selfGuided: true
        }
      ];
      setAllCategories(fallbackCategories);
      setDeselectedCategories(fallbackCategories);
    } finally {
      setLoading(false);
    }
  };

  // Initialize demo data
  React.useEffect(() => {
    if (currentUser?.token) {
      getQuestionCategoriesData();
    } else {
      // If no user token, use fallback data after a short delay
      setTimeout(() => {
        getQuestionCategoriesData();
      }, 500);
    }
  }, [currentUser]);

  // Handle checkboxes when modal opens (matching production)
  React.useEffect(() => {
    if (currentModal === 'category-private-notes') {
      handleCheckboxes();
    }
  }, [currentModal]);

  // Demo helper function to sync checkboxes with selected categories
  const handleCheckboxes = () => {
    for (const category of selectedCategories) {
      let categoryCheckbox = (Array.from(document.querySelectorAll('input[type="checkbox"]'))).find(i => i.getAttribute("data-category-id") === category.id.toString());

      if (categoryCheckbox) {
        categoryCheckbox.checked = true;
      }
    }
  };

  // Demo modal handlers
  const openModal = (e) => {
    e.preventDefault();
    const modalId = e.target.getAttribute('data-modal-id');
    setCurrentModal(modalId);
  };

  const closeModal = () => {
    setCurrentModal(null);
  };

  // Demo category selection (matching production signatures)
  const selectCategory = (event) => {
    event.preventDefault();
    setFormChanged(true);

    let categoryId = event.target.getAttribute('data-category-id');
    let category = deselectedCategories.find((obj) => {
      return obj.id.toString() === categoryId;
    });

    if (category && selectedCategories.length < 3) {
      let newInputs = { ...inputs };
      newInputs['isConversationTopic--' + category.id] = true;
      setInputs(newInputs);

      setSelectedCategories(prev => [...prev, category]);
      setDeselectedCategories(prev => prev.filter(cat => cat.id !== category.id));
    }
  };

  const deselectCategory = (event) => {
    event.preventDefault();
    setFormChanged(true);

    let categoryId = event.target.parentNode.getAttribute('data-category-id');
    let category = selectedCategories.find((obj) => {
      return obj.id.toString() === categoryId;
    });

    if (category) {
      let newInputs = { ...inputs };
      newInputs['isConversationTopic--' + category.id] = false;
      setInputs(newInputs);

      setDeselectedCategories(prev => [...prev, category]);
      setSelectedCategories(prev => prev.filter(cat => cat.id !== category.id));
    }
  };

  const selectCategories = () => {
    setFormChanged(true);

    // Clear current selected categories
    let selectedCategoriesCopy = selectedCategories.slice();

    // Get all checked categories from the modal
    let allCheckedCategories = Array.from(document.querySelectorAll('input[type="checkbox"]:checked'))
      .map(checkbox => allCategories.find((obj) => {
        return obj.id.toString() === checkbox.getAttribute('data-category-id');
      }))
      .filter(Boolean); // Remove undefined entries

    // Limit to 3 categories
    let categories = allCheckedCategories.slice(0, 3);

    // Update selected and deselected categories
    setSelectedCategories(categories);
    setDeselectedCategories(allCategories.filter(cat =>
      !categories.some(selected => selected.id === cat.id)
    ));

    // Update form inputs
    let newInputs = { ...inputs };

    // Clear previous selections
    for (const category of selectedCategoriesCopy) {
      if (category) {
        newInputs['isConversationTopic--' + category.id] = false;
      }
    }

    // Set new selections
    for (const category of categories) {
      if (category) {
        newInputs['isConversationTopic--' + category.id] = true;
      }
    }

    setInputs(newInputs);
    closeModal();
  };

  // Demo form handlers
  const handleChange = (e) => {
    setInputs(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
    setFormChanged(true);
  };

  const handleSave = () => {
    setSaving(true);

    // Update demo phase to topics selected
    updateDemoPhase('topics-selected');

    setTimeout(() => {
      setSaving(false);
      nextDemoStep();

      navigate('/demo/confirmation', {
        state: {
          flashMessage: {
            message: 'Demo: Your topics have been saved! In the real app, your manager would be notified.',
            className: 'notice'
          }
        }
      });
    }, 1000);
  };

  // Demo helper functions
  const questionResultsContent = (categoryId, field) => {
    if (inputs[field + '--' + categoryId]) {
      return inputs[field + '--' + categoryId];
    }
    return null;
  };

  return (
    <>
      {/* Global styles for demo category colors */}
      <style>
        {`
          .category-form-field__title--change {
            color: var(--color--green-turquoise--800) !important;
          }
          .category-form-field__title--relationships {
            color: var(--color--green-alternative--500) !important;
          }
          .category-form-field__title--demands {
            color: var(--color--green--500) !important;
          }
          .category-form-field__title--role {
            color: var(--color--green-turquoise-alternative--500) !important;
          }
          .category-form-field__title--peer-support {
            color: var(--color--green-turquoise-alternative-dark--500) !important;
          }
          .category-form-field__title--management-support {
            color: var(--color--blue--500) !important;
          }
          .category-form-field__title--control {
            color: var(--color--blue--300) !important;
          }
          .category-form-field__title--personalised,
          .category-form-field__title--12e9af3e-fb1d-4d4a-952f-9f91b5f5ad20,
          .category-form-field__title--1c3ab099-91c9-4e78-9fb4-850fa64599e0,
          .category-form-field__title--757cb486-0cee-4e3a-b885-ee6df58cffbd,
          .category-form-field__title--a3caeb12-192b-4570-9e21-1ce30f4c99c9,
          .category-form-field__title--personalised-1,
          .category-form-field__title--personalised-2 {
            color: var(--color--brand-secondary) !important;
          }
        `}
      </style>
      <ProgressBar loading={loading || saving} />
      <Header />
      <FlashMessage object={flashMessageObject} columnWrap={true} />
      <ErrorRender object={null} />

      {!loading && (
        <div className="grid-container">
          <Grid className="grid-padding-x align-justify">
            <Cell small={12}>
              <div className="page-content">
                <form className="basic-form">
                  {submissionUnlocked && guidedRoute === false && (
                    <>
                      <div className="page-notice">
                        <h1 className="page-notice__title">Self-reflection route</h1>
                        <div className="page-notice__content">
                          <p>
                            Please consider up to the three characteristics you would like to take to
                            your meeting. You can choose from the 7 characteristics listed or include
                            specific issues of your own.
                          </p>

                          <p>
                            It is really helpful to think about what specific aspects of each work
                            characteristic is important to discuss with your manager and also what are
                            the effects of this on you.
                          </p>
                        </div>
                      </div>
                      <div style={{ position: 'relative' }}>
                        <button
                          type="button"
                          className="page-content__button--medium page-content__button--back mb-3"
                          id="page-navigation-button--go-back"
                          onClick={handleGoBack}
                        >
                          Go back
                        </button>

                        <button
                          type="button"
                          className="page-content__button--medium mb-3"
                          id="page-navigation-button--save-changes"
                          onClick={handleSave}
                          disabled={!formChanged || saving}
                          style={{ position: 'absolute', right: '0', bottom: '0' }}
                        >
                          Save Changes
                        </button>
                      </div>

                      <div className="page-content__styles">
                        <h2 className="page-content__subtitle page-content__subtitle--section">
                          What you need to do
                        </h2>

                        <p>
                          Please choose up to three topics from the list below and then press submit.
                        </p>
                      </div>
                    </>
                  )}
                </form>

                {(submissionUnlocked || selectedCategories.length > 0) && (
                  <form className="basic-form" id="selected-categories">
                    <div className="question-categories-choice">
                      {submissionUnlocked && (
                        <>
                          <h2 className="question-categories-choice__title">
                            1) Review the conversation topics
                          </h2>

                          <div className="question-categories-choice__styles">
                            <p className="queestion-categories-choice__styles--microcopy">
                              When considering the topics to take to your manager meeting, please
                              take time to explore them and reflect upon whether and how they
                              cause you stress.
                              We have a useful guide on how to complete your team member prep in
                              our resources area.{' '}
                              <Link
                                to={process.env.PUBLIC_URL + '/Team Member Preparation Guidance.pdf'}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                View team member prep guide.
                              </Link>
                            </p>
                          </div>
                        </>
                      )}

                      <Grid className="grid-padding-x">
                        {selectedCategories?.map((questionCategory) => (
                          <Cell
                            small={12}
                            large={4}
                            key={`selected-category-${questionCategory.id}`}
                            style={{ display: 'flex', flexDirection: 'column' }}
                          >
                            <QuestionCategory
                              categoryFormButtons={categoryFormButtons}
                              closeModal={closeModal}
                              currentModal={currentModal}
                              deselectCategory={deselectCategory}
                              handleChange={handleChange}
                              openModal={openModal}
                              questionCategory={questionCategory}
                              questionResultsContent={questionResultsContent}
                              selectCategory={null}
                              selectedCategories={selectedCategories}
                              submissionUnlocked={submissionUnlocked}
                              showSummary={true}
                              showNotes={false}
                            />
                            {submissionUnlocked && (
                              <div className="category-notes-outer">
                                <textarea
                                  className="category-notes"
                                  placeholder="Topic Notes..."
                                  value={inputs[`notes--${questionCategory.id}`] || ''}
                                  data-complete="true"
                                  onChange={handleChange}
                                  name={`notes--${questionCategory.id}`}
                                />
                              </div>
                            )}
                          </Cell>
                        ))}

                        {submissionUnlocked &&
                          selectedCategories.length < 3 &&
                          Array.from({ length: 3 - selectedCategories.length }).map((_, i) => (
                            <Cell small={12} large={4} key={'pending-category-' + i}>
                              <div className="category-form-field-wrap">
                                <div className="category-form-field category-form-field--empty">
                                  <HashLink
                                    to="#deselected-categories"
                                    className="category-form-field__button category-form-field__button--secondary"
                                    id="page-function-button--add"
                                    onClick={openModal}
                                    data-modal-id="category-private-notes"
                                  >
                                    Add topic
                                    <PlusCircle
                                      size={21}
                                      className="category-form-field__button-icon category-form-field__button-icon--empty util--hide-icon"
                                    />
                                  </HashLink>
                                </div>
                              </div>
                            </Cell>
                          ))}
                      </Grid>
                    </div>

                    {submissionUnlocked && (
                      <>
                        <div className="question-categories-choice">
                          <h2 className="question-categories-choice__title">2) General Notes</h2>
                          <div className="question-categories-choice__styles question-categories-choice__styles--microcopy">
                            <p className="queestion-categories-choice__styles--microcopy">
                              Take time to understand the topics and review the examples then make any notes that you feel will help you to ensure that you include everything you would like to communicate in the meaning.
                            </p>
                          </div>
                          <div className="category-notes-outer">
                            <textarea
                              id="general_notes"
                              name="general_notes"
                              placeholder="General Notes..."
                              className="category-notes"
                              data-complete="true"
                              value={inputs.general_notes || ''}
                              onChange={handleChange}
                            />
                          </div>
                        </div>

                        <div className="question-categories-choice">
                          <h2 className="question-categories-choice__title">
                            3) Submit your prep
                          </h2>

                          <Grid className="grid-padding-x">
                            <Cell small={12} large={9}>
                              <div className="question-categories-choice__styles question-categories-choice__styles--microcopy">
                                <p className="queestion-categories-choice__styles--microcopy">When you are happy with the topics you have chosen</p>
                              </div>
                            </Cell>
                            <Cell small={12} large={3} className="text-right">
                              {formChanged && (
                                <>
                                  {selectedCategories.length >= 1 && (
                                    <div className="basic-form__actions basic-form__actions--inline">
                                      <input
                                        value="Next Step"
                                        data-complete="true"
                                        type="button"
                                        className="basic-form__button basic-form__button--inline"
                                        onClick={handleSave}
                                        id="page-form-button--complete"
                                      />
                                    </div>
                                  )}
                                </>
                              )}
                            </Cell>
                          </Grid>
                        </div>                        {currentModal === 'category-private-notes' && (
                          <div className="modal-backdrop fade show" style={{ zIndex: 1040 }}></div>
                        )}

                        {currentModal === 'category-private-notes' && (
                          <div
                            className="modal fade show"
                            tabIndex="-1"
                            style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
                          >
                            <Modal show={currentModal === 'category-private-notes'} dialogClassName='new-modal-dialog' className='topic-modal'>
                              <Modal.Header className='justify-content-center'>
                                <div className="modal-header border-0" style={{ position: 'relative' }}>
                                  <h5 className="modal-title w-100 text-center">
                                    Select Your Conversation Topics (Max 3)
                                  </h5>
                                </div>
                                <button
                                  type="button"
                                  className="btn-close-categories"
                                  aria-label="Close"
                                  onClick={closeModal}
                                  style={{ position: 'absolute', top: '1rem', right: '1rem', zIndex: 2 }}
                                >
                                  <span aria-hidden="true">&times;</span>
                                </button>
                              </Modal.Header>

                              <Modal.Body>
                                <div className="category-form-modal">
                                  {submissionUnlocked && (
                                    <div className="grid-container">
                                      <Grid className="grid-padding-x">
                                        <Cell small={12}>
                                          {(selectedCategories || deselectedCategories) && (
                                            <Grid className="grid-padding-x">
                                              {[...selectedCategories, ...deselectedCategories].map((questionCategory) => (
                                                <Cell
                                                  small={12}
                                                  large={4}
                                                  key={'modal-question-' + questionCategory.id}
                                                >
                                                  <QuestionCategory
                                                    categoryFormButtons={categoryFormButtons}
                                                    closeModal={closeModal}
                                                    currentModal={currentModal}
                                                    deselectCategory={null}
                                                    handleChange={handleChange}
                                                    openModal={openModal}
                                                    questionCategory={questionCategory}
                                                    questionResultsContent={questionResultsContent}
                                                    selectCategory={selectCategory}
                                                    selectedCategories={selectedCategories}
                                                    submissionUnlocked={submissionUnlocked}
                                                    showSummary={true}
                                                    showNotes={true}
                                                  />
                                                </Cell>
                                              ))}
                                            </Grid>
                                          )}
                                        </Cell>
                                      </Grid>
                                    </div>
                                  )}
                                </div>
                              </Modal.Body>
                              <Modal.Footer className='justify-content-center'>
                                <div className="modal-footer border-0 justify-content-center">
                                  <button
                                    className="page-content__button--medium mb-3"
                                    id="page-navigation-button--save-changes"
                                    onClick={selectCategories}
                                  >
                                    Save Chosen Topics
                                  </button>
                                </div>
                              </Modal.Footer>
                            </Modal>
                          </div>
                        )}
                      </>
                    )}
                  </form>
                )}
              </div>
            </Cell>
          </Grid>
        </div>
      )}

      {/* Individual category content modals */}
      {allCategories.map((category) => (
        currentModal === `question-category-content--${category.id}` && (
          <div
            key={`modal-${category.id}`}
            className="modal fade show d-block"
            tabIndex="-1"
            style={{ zIndex: 1050, backgroundColor: 'rgba(0,0,0,0.5)' }}
          >
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content" style={{ borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ position: 'relative', padding: '0 24px 0 24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', minHeight: 56, borderBottom: '1px solid #e5e5e5', padding: '16px 0' }}>
                    <div style={{ flex: 1, textAlign: 'center', fontWeight: 700, fontSize: 20 }}>
                      {category.name}
                    </div>
                    <button
                      type="button"
                      aria-label="Close"
                      onClick={closeModal}
                      style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', lineHeight: 1 }}
                    >
                      &times;
                    </button>
                  </div>
                  <div style={{ padding: '20px 0' }}>
                    <QuestionCategoryContent categoryId={category.id} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      ))}
    </>
  );
}
